import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "../Layout";
import { apiGetJson, clamp, fmtDate } from "../gemv2/gemV2Api";
import { adminGetJson } from "../admin/adminApi";
import { AdminLoadingBar, AdminLoadingStyles, AdminSpinner } from "../admin/AdminLoadingUi";

const COLORS = {
  text: "#1f2937",
  muted: "#6b7280",
  border: "#e5e7eb",
  border2: "#f3f4f6",
  bgHead: "#f9fafb",
  link: "#2563eb",
};

const NUMERIC_KEYS = new Set([
  "estimated_cost",
  "emd_amount",
  "emd_inr",
  "grand_total",
  "bid_validity_period_days",
  "bidder_sno",
]);

const DATE_KEYS = new Set([
  "bid_submission_closing",
  "bid_opening",
  "tender_publishing_date_time",
  "pre_bid_query_last_date",
  "detail_bid_submission_closing",
  "bid_validity_expiry_date",
]);

const BOOL_KEYS = new Set(["has_view", "has_bid_details", "price_excel_downloaded"]);

const WIDE_KEYS = new Set([
  "name_of_work",
  "detail_name_of_work",
  "tender_publishing_office",
  "office_inviting_bids",
  "tia_address",
  "downloaded_file",
  "excel_file",
  "emd_in_favour_of",
]);

const containerStyle = { maxWidth: 1600, margin: "0 auto", padding: 16, color: COLORS.text };
const cardStyle = { border: `1px solid ${COLORS.border}`, borderRadius: 12, padding: 12, background: "#fff" };
const rowStyle = { display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" };
const inputStyle = { padding: "10px 12px", borderRadius: 10, border: "1px solid #d1d5db" };
const searchStyle = { ...inputStyle, minWidth: 340 };
const btnStyle = { padding: "9px 12px", borderRadius: 10, border: "1px solid #d1d5db", background: "#fff", cursor: "pointer", color: COLORS.text };
const btnActiveStyle = { ...btnStyle, border: "1px solid #111827", fontWeight: 800 };
const smallMuted = { color: COLORS.muted, fontSize: 12 };
const tableWrap = { overflowX: "auto", borderRadius: 12, border: `1px solid ${COLORS.border}` };
const thStyle = { textAlign: "left", padding: 10, background: COLORS.bgHead, borderBottom: `1px solid ${COLORS.border}`, whiteSpace: "normal", verticalAlign: "top", lineHeight: 1.2 };
const tdStyle = { padding: 10, borderBottom: `1px solid ${COLORS.border2}`, verticalAlign: "top", wordBreak: "break-word" };
const filterInputStyle = { width: "100%", minWidth: 0, padding: "6px 7px", borderRadius: 8, border: "1px solid #d1d5db" };
const filterDateStyle = { ...filterInputStyle, padding: "4px 6px" };
const DEFAULT_SORT_FIELD = "tender_id";
const DEFAULT_SORT_ORDER = "DESC";

function useDebouncedValue(value, delayMs = 350) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

function buildQuery(params = {}) {
  const usp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v == null) return;
    const s = String(v).trim();
    if (!s) return;
    usp.set(k, s);
  });
  const qs = usp.toString();
  return qs ? `?${qs}` : "";
}

function money(v) {
  if (v == null || v === "") return "-";
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v);
  return n.toLocaleString("en-IN", { maximumFractionDigits: 2 });
}

function text(v) {
  return v == null || v === "" ? "" : String(v);
}

function splitTerms(raw = "") {
  return String(raw).split(",").map((s) => s.trim()).filter(Boolean);
}

function escapeRegExp(str = "") {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function uniqueTerms(terms = []) {
  const seen = new Set();
  const out = [];
  for (const term of terms) {
    const clean = String(term || "").trim();
    if (!clean) continue;
    const key = clean.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(clean);
  }
  return out;
}

function HighlightText({ value, terms }) {
  const raw = text(value);
  if (!raw) return <>-</>;

  const clean = uniqueTerms(terms).filter((term) => term.length >= 2);
  if (!clean.length) return <>{raw}</>;

  let regex;
  try {
    regex = new RegExp(`(${clean.map(escapeRegExp).join("|")})`, "ig");
  } catch {
    return <>{raw}</>;
  }

  const parts = raw.split(regex);
  return (
    <>
      {parts.map((part, idx) => {
        const hit = clean.some((term) => term.toLowerCase() === part.toLowerCase());
        return hit ? <mark key={idx} style={{ backgroundColor: "#fef08a", padding: "0 1px", borderRadius: 3 }}>{part}</mark> : <React.Fragment key={idx}>{part}</React.Fragment>;
      })}
    </>
  );
}

function getColumnWidth(key) {
  if (key === "price_rank") return 80;
  if (key === "name_of_work") return 460;
  if (key === "tender_id") return 120;
  if (WIDE_KEYS.has(key)) return 260;
  if (DATE_KEYS.has(key)) return 180;
  if (NUMERIC_KEYS.has(key)) return 140;
  if (key.includes("tender_id") || key.includes("nit_rfp_no")) return 170;
  if (key.includes("bidder") || key.includes("contractor")) return 220;
  return 155;
}

function renderCellValue(row, col, terms) {
  const value = row[col.key];
  if (NUMERIC_KEYS.has(col.key)) return money(value);
  if (DATE_KEYS.has(col.key)) return fmtDate(value) || "-";
  if (BOOL_KEYS.has(col.key)) return value == null ? "-" : (value ? "Yes" : "No");
  return <HighlightText value={value} terms={terms} />;
}

function SortHeader({ col, activeField, activeOrder, onSort, loading }) {
  const active = activeField === col.key;
  const arrow = active ? (activeOrder === "ASC" ? " ▲" : " ▼") : " ↕";
  const width = getColumnWidth(col.key);
  return (
    <th
      style={{ ...thStyle, cursor: loading ? "default" : "pointer", opacity: loading ? 0.7 : 1, fontWeight: active ? 800 : 700, width, minWidth: width, maxWidth: width }}
      onClick={() => !loading && onSort(col.key)}
      title={loading ? "" : "Click to sort"}
    >
      {col.header || col.label || col.key}
      <span style={{ color: active ? COLORS.text : "#9ca3af" }}>{arrow}</span>
    </th>
  );
}

function Pagination({ page, totalPages, onPage, loading }) {
  const safeTotal = Math.max(1, totalPages || 1);
  return (
    <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
      <button style={btnStyle} onClick={() => onPage(clamp(page - 1, 1, safeTotal))} disabled={page <= 1 || loading}>Prev</button>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <span style={smallMuted}>Page</span>
        <input
          style={{ ...inputStyle, width: 90, minWidth: 90 }}
          value={page}
          onChange={(e) => {
            const v = parseInt(e.target.value || "1", 10);
            onPage(clamp(Number.isNaN(v) ? 1 : v, 1, safeTotal));
          }}
        />
        <span style={smallMuted}>of {safeTotal}</span>
      </div>
      <button style={btnStyle} onClick={() => onPage(clamp(page + 1, 1, safeTotal))} disabled={page >= safeTotal || loading}>Next</button>
    </div>
  );
}

export default function CpwdTenderResultsPage({ admin = false }) {
  const [view, setView] = useState("summary");
  const [searchInput, setSearchInput] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(admin ? 50 : 20);
  const [sortField, setSortField] = useState(DEFAULT_SORT_FIELD);
  const [sortOrder, setSortOrder] = useState(DEFAULT_SORT_ORDER);
  const [filters, setFilters] = useState({});
  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [uniqueTenders, setUniqueTenders] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const search = useDebouncedValue(searchInput.trim(), 300);
  const debouncedFilters = useDebouncedValue(filters, 350);

  useEffect(() => {
    setPage(1);
  }, [view, search, debouncedFilters, limit, sortField, sortOrder]);

  const query = useMemo(() => {
    const q = {
      view,
      page,
      limit,
      search,
      sortField,
      sortOrder: sortOrder.toLowerCase(),
    };

    columns.forEach((col) => {
      const key = col.key;
      if (DATE_KEYS.has(key)) {
        if (debouncedFilters[`${key}_from`]) q[`${key}From`] = debouncedFilters[`${key}_from`];
        if (debouncedFilters[`${key}_to`]) q[`${key}To`] = debouncedFilters[`${key}_to`];
      } else if (NUMERIC_KEYS.has(key)) {
        if (debouncedFilters[key]) q[`min_${key}`] = debouncedFilters[key];
      } else if (debouncedFilters[key]) {
        q[`f_${key}`] = debouncedFilters[key];
      }
    });

    return buildQuery(q);
  }, [view, page, limit, search, sortField, sortOrder, columns, debouncedFilters]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        setError("");
        const endpoint = admin ? "/api/admin/cpwd-tender-results" : "/api/cpwd-tender-results";
        const json = admin
          ? await adminGetJson(`${endpoint}${query}`)
          : await apiGetJson(`${endpoint}${query}`, "results");

        if (!mounted) return;
        setColumns(Array.isArray(json?.columns) ? json.columns : []);
        setRows(Array.isArray(json?.rows) ? json.rows : (Array.isArray(json?.data) ? json.data : []));
        setTotalRows(Number(json?.totalRows || json?.total || 0));
        setUniqueTenders(Number(json?.uniqueTenders || 0));
        setTotalPages(Math.max(1, Number(json?.totalPages || 1)));
      } catch (e) {
        if (!mounted) return;
        setError(e.message || String(e));
        setRows([]);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [admin, query]);

  const searchTerms = useMemo(() => splitTerms(searchInput), [searchInput]);
  const highlightTermsByColumn = useMemo(() => {
    const out = {};
    columns.forEach((col) => {
      out[col.key] = uniqueTerms([...searchTerms, ...splitTerms(filters[col.key] || "")]);
    });
    return out;
  }, [columns, searchTerms, filters]);

  function onSort(field) {
    if (sortField === field) {
      setSortOrder((s) => (s === "ASC" ? "DESC" : "ASC"));
    } else {
      setSortField(field);
      setSortOrder("DESC");
    }
    setPage(1);
  }

  function setViewAndReset(nextView) {
    setView(nextView);
    setSortField(DEFAULT_SORT_FIELD);
    setSortOrder(DEFAULT_SORT_ORDER);
    setPage(1);
  }

  function setFilter(key, value) {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  }

  function clearAllFilters() {
    setSearchInput("");
    setFilters({});
    setSortField(DEFAULT_SORT_FIELD);
    setSortOrder(DEFAULT_SORT_ORDER);
    setPage(1);
  }

  const title = admin ? "CPWD Tender Results - Admin" : "CPWD Tender Results";
  const subtitle = view === "summary" ? "Summary (L1 only)" : "All bidders";

  return (
    <Layout>
      <div style={containerStyle}>
        {admin ? <AdminLoadingStyles /> : (
          <style>{`
            .tm-loadingbar-wrap{height:3px;border-radius:999px;background:#eef2ff;overflow:hidden;margin-top:10px}
            .tm-loadingbar{height:100%;width:40%;background:#111827;transform:translateX(-120%);animation:tmbar 1.1s infinite}
            @keyframes tmbar{0%{transform:translateX(-120%);opacity:.35}50%{opacity:.9}100%{transform:translateX(260%);opacity:.35}}
            .tm-spinner{width:14px;height:14px;border-radius:999px;border:2px solid #e5e7eb;border-top-color:#111827;animation:tmspin .8s linear infinite;display:inline-block}
            @keyframes tmspin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}
          `}</style>
        )}

        <div style={{ ...cardStyle, marginBottom: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
            <div>
              {admin ? (
                <Link to="/admin" style={{ color: "#64748b", textDecoration: "none", fontSize: 14 }}>{"<-"} Admin Panel</Link>
              ) : null}
              <div style={{ fontSize: 20, fontWeight: 800, display: "flex", gap: 10, alignItems: "center", marginTop: admin ? 6 : 0 }}>
                <span>{title} - {subtitle}</span>
                {admin ? <AdminSpinner loading={loading} /> : (loading ? <span className="tm-spinner" title="Loading" /> : null)}
              </div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontWeight: 800 }}>{totalRows.toLocaleString()} rows</div>
              <div style={smallMuted}>{uniqueTenders.toLocaleString()} tenders</div>
            </div>
          </div>

          {admin ? <AdminLoadingBar loading={loading} /> : (loading ? <div className="tm-loadingbar-wrap"><div className="tm-loadingbar" /></div> : null)}

          <div style={{ marginTop: 12, ...rowStyle }}>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button style={view === "summary" ? btnActiveStyle : btnStyle} onClick={() => setViewAndReset("summary")}>Summary</button>
              <button style={view === "all" ? btnActiveStyle : btnStyle} onClick={() => setViewAndReset("all")}>All</button>
            </div>

            <input
              style={searchStyle}
              placeholder="Search across all visible columns (comma-separated)"
              value={searchInput}
              onChange={(e) => { setSearchInput(e.target.value); setPage(1); }}
            />

            <select
              style={inputStyle}
              value={limit}
              onChange={(e) => {
                setLimit(clamp(parseInt(e.target.value || "50", 10), 1, 200));
                setPage(1);
              }}
            >
              <option value={20}>20 / page</option>
              <option value={50}>50 / page</option>
              <option value={100}>100 / page</option>
              <option value={200}>200 / page</option>
            </select>

            <button style={btnStyle} onClick={clearAllFilters}>Clear Filters</button>

            <div style={{ marginLeft: "auto" }}>
              <Pagination page={page} totalPages={totalPages} onPage={setPage} loading={loading} />
            </div>
          </div>

          {error ? <div style={{ marginTop: 10, color: "#b91c1c", fontWeight: 700 }}>Error: {error}</div> : null}
        </div>

        <div style={tableWrap}>
          <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
            <thead>
              <tr>
                {columns.map((col) => (
                  <SortHeader key={col.key} col={col} activeField={sortField} activeOrder={sortOrder} onSort={onSort} loading={loading} />
                ))}
              </tr>
              <tr>
                {columns.map((col) => {
                  const width = getColumnWidth(col.key);
                  const baseTh = { ...thStyle, background: "#fff", padding: 6, width, minWidth: width, maxWidth: width };

                  if (DATE_KEYS.has(col.key)) {
                    return (
                      <th key={`${col.key}_filter`} style={baseTh}>
                        <div style={{ display: "grid", gap: 4 }}>
                          <input type="date" style={filterDateStyle} value={filters[`${col.key}_from`] || ""} onChange={(e) => setFilter(`${col.key}_from`, e.target.value)} title="From date" />
                          <input type="date" style={filterDateStyle} value={filters[`${col.key}_to`] || ""} onChange={(e) => setFilter(`${col.key}_to`, e.target.value)} title="To date" />
                        </div>
                      </th>
                    );
                  }

                  return (
                    <th key={`${col.key}_filter`} style={baseTh}>
                      <input
                        type={NUMERIC_KEYS.has(col.key) ? "number" : "text"}
                        step={NUMERIC_KEYS.has(col.key) ? "0.01" : undefined}
                        style={filterInputStyle}
                        placeholder={NUMERIC_KEYS.has(col.key) ? "Min" : "Filter"}
                        value={filters[col.key] || ""}
                        onChange={(e) => setFilter(col.key, e.target.value)}
                      />
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {!loading && rows.length === 0 ? (
                <tr>
                  <td colSpan={Math.max(1, columns.length)} style={{ ...tdStyle, color: COLORS.muted }}>
                    No rows found for current filters.
                  </td>
                </tr>
              ) : null}

              {rows.map((row, idx) => (
                <tr key={`${row.row_hash || idx}`}>
                  {columns.map((col) => {
                    const width = getColumnWidth(col.key);
                    const alignRight = NUMERIC_KEYS.has(col.key);
                    return (
                      <td
                        key={col.key}
                        style={{ ...tdStyle, width, minWidth: width, maxWidth: width, textAlign: alignRight ? "right" : "left" }}
                        title={row[col.key] == null ? "" : String(row[col.key])}
                      >
                        {renderCellValue(row, col, highlightTermsByColumn[col.key] || searchTerms)}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div style={{ ...cardStyle, marginTop: 12 }}>
          <Pagination page={page} totalPages={totalPages} onPage={setPage} loading={loading} />
        </div>
      </div>
    </Layout>
  );
}
